import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Restaurant} from '../../../../../core/models/restaurant.model';
import {PaymentStore} from '../../../../../core/stores/payment.store';
import {environment} from '../../../../../../environments/environment';
import {ATRIUM_LOGIN, STORAGE_CUSTOMER_DATA} from '../../../../../core/constants';
import {CustomerService} from '../../../../../core/services/customer.service';
import {AtriumInfo} from '../../../../../core/models/customer.model';
import {takeWhile} from 'rxjs/operators';
import {StorageService} from '../../../../../core/services/storage.service';
import {Subject} from 'rxjs';

@Component({
  selector: 'app-atrium',
  templateUrl: './atrium.component.html',
  styleUrls: ['./atrium.component.scss'],
})
export class AtriumComponent implements OnInit, OnDestroy {
  @Input() restaurant: Restaurant = new Restaurant();
  @Input() total: number = null;
  @Output() atriumPartial: EventEmitter<boolean> = new EventEmitter();
  @Output() public paymentItemError = new EventEmitter<string>();
  public atriumCampusId: string = null;
  public atriumInfo: AtriumInfo = new AtriumInfo();
  public isSpinner: boolean;
  public isPartial: boolean;
  public isConnecting: boolean;
  private destroyed$ = new Subject<void>();
  private atriumCheckInterval: ReturnType<typeof setInterval>;

  constructor(
    public paymentStore: PaymentStore,
    public customerService: CustomerService,
    private storageService: StorageService,
  ) { }

  public get lowBalance(): boolean {
    return (this.total * 100) > this.atriumInfo.balance;
  }

  ngOnInit() {
    this.atriumInfo$();
    this.checkVerification();
  }
  ngOnDestroy(): void {
    this.isSpinner = false;
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public isPartialPayment() {
    this.isPartial = !this.isPartial;
    this.atriumPartial.emit(this.isPartial);
  }

  public getFormattedPrice(price: number): string {
    return (price).toFixed(2);
  }
  public connectAtriumAccount(): void {
    this.isConnecting = true;
    const callbackUrl = `atrium_callback?orderBotUserId=${this.paymentStore.customer.orderBotUserId}&locationId=${this.restaurant.locationId}`;
    const encodedCallbackUrl = encodeURIComponent(callbackUrl);
    const url = `${this.restaurant.atriumConnectUrl}&url=${environment.apiUrl}${encodedCallbackUrl}`;
    window.open(
      url,
      '_blank',
    );
    this.checkLocalStoragePeriodically();
  }

  public cancelConnect(): void {
    this.isConnecting = false;
    this.clearLocalStorageInterval();
  }

  public saveAtriumInfo(atriumCampusId: string): void {
    if (this.paymentStore.customer.orderBotUserId) {
      this.isSpinner = true;
      const info = {
        campus_id: atriumCampusId,
        location_id: this.restaurant.locationId
      };
      this.customerService.saveAtriumInfo(this.paymentStore.customer.orderBotUserId, info)
        .subscribe({
          next: (atriumInfo) => {
            this.paymentItemError.emit(null);
            this.atriumInfo = atriumInfo;
            this.isSpinner = false;
          },
          error: (e) => {
            this.isSpinner = false;
          }
        });
    }
  }

  public async removeAtriumInfo(): Promise<void> {
    const customerStorageData = await this.storageService.get(STORAGE_CUSTOMER_DATA);
    if (customerStorageData) {
      const { token } = customerStorageData;
      this.isSpinner = true;
      const info = {
        token: token,
        location_id: this.restaurant.locationId
      };
      this.customerService.deleteAtriumInfo(this.paymentStore.customer.orderBotUserId, info)
        .subscribe({
          next: () => {
            this.atriumCampusId = null;
            this.atriumInfo = new AtriumInfo();
            this.customerService.atriumInfo = new AtriumInfo();
            this.isSpinner = false;
          },
          error: (e) => {
            this.isSpinner = false;
          }
        });
    }
  }

  private checkLocalStoragePeriodically(): void {
    this.clearLocalStorageInterval();

    this.atriumCheckInterval = setInterval(() => {
      const data = localStorage.getItem(ATRIUM_LOGIN);
      if (data) {
        this.clearLocalStorageInterval();
        this.getAtriumInfo();
        localStorage.removeItem(ATRIUM_LOGIN);
      }
    }, 5000);
  }

  private checkVerification(): void {
    this.paymentStore.verificationCustomer$
      .pipe(
        takeWhile(verification => !verification, true)
      )
      .subscribe(verification => {
        if (verification) {
          this.getAtriumInfo();
        }
      });
  }

  private async getAtriumInfo(): Promise<void> {
    const customerStorageData = await this.storageService.get(STORAGE_CUSTOMER_DATA);
    if (customerStorageData) {
      this.isSpinner = true;
      const { token } = customerStorageData;
      const info = {
        token: token,
        location_id: this.restaurant.locationId
      };
      this.customerService.getAtriumInfo(this.paymentStore.customer.orderBotUserId, info)
        .subscribe({
          next: (atriumInfo) => {
            this.paymentItemError.emit(null);
            this.atriumInfo = atriumInfo;
            this.isSpinner = false;
            this.isConnecting = false;
          },
          error: (e) => {
            this.isSpinner = false;
            this.isConnecting = false;
          }
        });
    }
  }

  private clearLocalStorageInterval(): void {
    clearInterval(this.atriumCheckInterval);
    this.atriumCheckInterval = null;
  }

  private atriumInfo$(): void {
    this.customerService.atriumInfo$.subscribe(info => {
      this.atriumInfo = info;
    });
  }
}
